// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import type { Section } from './navModel';

/**
 * IAM actions that make a rail section usable (AB#35784). A section shows when the
 * user holds ANY of its actions: the BFF checks the same actions on the section's
 * routes, so a section without them would only open onto a 403.
 *
 * A section absent from this map has no permission of its own (Notes, Bills,
 * Onboarding, User management, Access, Fleet posture) and is not filtered here.
 */
export const SECTION_PERMISSIONS: Partial<Record<Section, readonly string[]>> = {
  home: ['aem.site.read'],
  site: ['twin.sites.read'],
  fleet: ['twin.sites.read', 'aem.site.read'],
  optimization: ['hitl.site.read'],
  workOrders: ['workorders.site.read'],
  sop: ['sop.site.read'],
};

/** Sections that make up the operations app; an admin with none of them is Admin Center only. */
export const OPERATIONS_SECTIONS: readonly Section[] = [
  'home',
  'site',
  'optimization',
  'workOrders',
];

const holds = (permissions: readonly string[], action: string): boolean =>
  permissions.includes(action) || permissions.includes('*');

/**
 * Whether the user's permissions open `section`. An empty list means /me gave us
 * none to judge by (not loaded, or a role IAM doesn't enumerate): the role map
 * decides then, so nobody loses navigation to an unknown.
 */
export function canAccessSection(section: Section, permissions: readonly string[]): boolean {
  const needed = SECTION_PERMISSIONS[section];
  if (!needed || permissions.length === 0) return true;
  return needed.some(action => holds(permissions, action));
}

/** True when the user's permissions open none of the operations sections. */
export function lacksOperationsAccess(permissions: readonly string[]): boolean {
  return permissions.length > 0 && !OPERATIONS_SECTIONS.some(s => canAccessSection(s, permissions));
}
